import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '../utils/toast';
import { CreditCard, Lock, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { planId, billingCycle } = location.state || {};
  
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  useEffect(() => {
    if (!planId) {
      toast.error('No plan selected');
      navigate('/subscription/plans');
      return;
    }
    fetchPlanDetails();
  }, [planId, navigate]);

  const fetchPlanDetails = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/plans`);
      const selectedPlan = response.data.plans.find(p => p.plan_id === planId);
      setPlan(selectedPlan);
    } catch (error) {
      toast.error('Failed to fetch plan details');
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Simulate payment processing
    setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${API}/subscriptions/upgrade`,
          {
            new_plan_id: planId,
            billing_cycle: billingCycle
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        setPaymentSuccess(true);
        toast.success(response.data.message);
        
        // Redirect after 3 seconds
        setTimeout(() => {
          navigate('/subscription');
        }, 3000);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Payment failed');
      } finally {
        setLoading(false);
      }
    }, 2000);
  };

  if (!plan) {
    return (
      <div className="min-h-screen bg-[#F5F6FA] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-[#F5F6FA] flex items-center justify-center">
        <Card className="max-w-md w-full p-8 text-center">
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
          <h2 className="text-[22px] font-bold mb-2" style={{ color: '#0056B3' }}>Payment Successful!</h2>
          <p className="text-gray-600 mb-6">Your subscription has been upgraded successfully.</p>
          <p className="text-sm text-gray-500">Redirecting to subscription dashboard...</p>
        </Card>
      </div>
    );
  }

  const price = billingCycle === 'monthly' ? plan.price_monthly : plan.price_yearly;
  const tax = price * 0.18;
  const total = price + tax;

  return (
    <div className="min-h-screen bg-[#F5F6FA]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>Checkout</h1>
            <Button onClick={() => navigate('/subscription/plans')} variant="outline">
              Cancel
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Order Summary */}
          <Card className="p-6">
            <h2 className="text-[18px] font-bold mb-4" style={{ color: '#0056B3' }}>Order Summary</h2>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Plan</span>
                <span className="font-medium">{plan.plan_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Billing Cycle</span>
                <span className="font-medium capitalize">{billingCycle}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">₹{price.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">GST (18%)</span>
                <span className="font-medium">₹{tax.toLocaleString('en-IN')}</span>
              </div>
              <div className="border-t pt-4 flex justify-between text-lg">
                <span className="font-bold">Total</span>
                <span className="font-bold" style={{ color: '#0056B3' }}>₹{total.toLocaleString('en-IN')}</span>
              </div>
            </div>
          </Card>

          {/* Payment Form */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[18px] font-bold" style={{ color: '#0056B3' }}>Payment Details</h2>
              <Lock className="w-5 h-5 text-gray-400" />
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-800">
                <strong>Demo Mode:</strong> This is a simulated payment. No actual charges will be made.
              </p>
            </div>

            <form onSubmit={handlePayment} className="space-y-4">
              <div>
                <Label>Cardholder Name</Label>
                <Input placeholder="John Doe" required />
              </div>

              <div>
                <Label>Card Number</Label>
                <div className="relative">
                  <CreditCard className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <Input className="pl-10" placeholder="1234 5678 9012 3456" required />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Expiry Date</Label>
                  <Input placeholder="MM/YY" required />
                </div>
                <div>
                  <Label>CVV</Label>
                  <Input placeholder="123" type="password" maxLength={3} required />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#0056B3] hover:bg-[#0056B3]/90"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Processing...
                  </>
                ) : (
                  `Pay ₹${total.toLocaleString('en-IN')}`
                )}
              </Button>
            </form>

            <p className="text-xs text-gray-500 text-center mt-4">
              <Lock className="w-3 h-3 inline mr-1" />
              Your payment information is secure
            </p>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default PaymentPage;
